export const PAGE_SIZE = 20;

export const DEFAULT_SORT = "price,asc";
export const DISTANCE_SORT = "distance,asc";

export const DEFAULT_RADIUS_KM = 20;
export const MIN_RADIUS_KM = 1;
export const MAX_RADIUS_KM = 100;
export const MAX_SEARCH_RADIUS_METERS = 100000;

export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 300;

export const SKELETON_DELAY_MS = 160;
export const SKELETON_MINIMUM_MS = 300;

export const SORT_OPTIONS = [
  { value: DEFAULT_SORT, label: "Price: Low to High" },
  { value: "price,desc", label: "Price: High to Low" },
  { value: DISTANCE_SORT, label: "Nearest First", needsLocation: true },
  { value: "createdAt,desc", label: "Newest First" },
];

export const INITIAL_FILTERS = Object.freeze({
  materialIds: [],
  minPrice: null,
  maxPrice: null,
  radiusKm: DEFAULT_RADIUS_KM,
  inStockOnly: false,
  sort: DEFAULT_SORT,
});

const BOUNDS_STRIPPED = [
  "page",
  "size",
  "sort",
  "minPrice",
  "maxPrice",
  "minVolume",
  "maxVolume",
];

export const hasCoordinates = (coordinates) =>
  Boolean(
    coordinates &&
      Number.isFinite(coordinates.latitude) &&
      Number.isFinite(coordinates.longitude)
  );

export const clampRadiusKm = (radiusKm) => {
  if (!Number.isFinite(radiusKm)) return null;

  return Math.min(MAX_RADIUS_KM, Math.max(MIN_RADIUS_KM, Math.round(radiusKm)));
};

export const radiusToMeters = (radiusKm) => {
  const clamped = clampRadiusKm(radiusKm);

  return clamped === null ? MAX_SEARCH_RADIUS_METERS : clamped * 1000;
};

export const normaliseFilters = (filters) => {
  const merged = { ...INITIAL_FILTERS, ...(filters || {}) };

  return {
    materialIds: [...new Set(merged.materialIds || [])].filter((id) =>
      Number.isFinite(Number(id))
    ),
    minPrice: Number.isFinite(merged.minPrice) ? merged.minPrice : null,
    maxPrice: Number.isFinite(merged.maxPrice) ? merged.maxPrice : null,
    radiusKm: merged.radiusKm === null ? null : clampRadiusKm(merged.radiusKm),
    inStockOnly: merged.inStockOnly === true,
    sort: merged.sort || DEFAULT_SORT,
  };
};

export const countActiveFilters = (filters) => {
  const values = normaliseFilters(filters);

  return (
    values.materialIds.length +
    (values.minPrice !== null || values.maxPrice !== null ? 1 : 0) +
    (values.radiusKm !== DEFAULT_RADIUS_KM ? 1 : 0) +
    (values.inStockOnly ? 1 : 0) +
    (values.sort !== DEFAULT_SORT ? 1 : 0)
  );
};

export const sortLabel = (sort) =>
  SORT_OPTIONS.find((option) => option.value === sort)?.label ||
  SORT_OPTIONS[0].label;

export const sortOptionsFor = (located) =>
  SORT_OPTIONS.filter((option) => located || !option.needsLocation);

export const normaliseSort = (sort, located) =>
  !located && sort === DISTANCE_SORT ? DEFAULT_SORT : sort || DEFAULT_SORT;

export const searchTermFor = (term) => {
  const trimmed = String(term || "").trim();

  return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : null;
};

export const buildSearchParams = ({
  containerOptionId,
  categoryCode,
  term,
  filters,
  coordinates,
  page = 0,
  size = PAGE_SIZE,
}) => {
  const values = normaliseFilters(filters);
  const located = hasCoordinates(coordinates);

  const params = {
    containerOptionId,
    page,
    size,
    sort: normaliseSort(values.sort, located),
  };

  if (categoryCode) params.category = categoryCode;

  const name = searchTermFor(term);
  if (name) params.name = name;

  if (values.materialIds.length) {
    params.containerMaterialId = values.materialIds.map(Number);
  }

  if (values.minPrice !== null) params.minPrice = values.minPrice;
  if (values.maxPrice !== null) params.maxPrice = values.maxPrice;

  if (values.inStockOnly) params.inStockProducts = true;

  if (located) {
    params["location.latitude"] = coordinates.latitude;
    params["location.longitude"] = coordinates.longitude;
    params["location.distanceMeters"] = radiusToMeters(values.radiusKm);
  }

  return params;
};

export const buildBoundsParams = (params) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(([key]) => !BOUNDS_STRIPPED.includes(key))
  );

export const stableKey = (params) =>
  JSON.stringify(
    Object.keys(params || {})
      .sort()
      .map((key) => [key, params[key]])
  );

export const pagelessKey = (params) => {
  const { page, ...rest } = params || {};

  return stableKey(rest);
};

export const priceStep = (max) => {
  if (!Number.isFinite(max) || max <= 100) return 1;
  if (max <= 1000) return 5;
  if (max <= 10000) return 25;

  return 100;
};

export const clearPrice = (filters) => ({
  ...normaliseFilters(filters),
  minPrice: null,
  maxPrice: null,
});
